import {
  CONSUMER_POLICY_REVIEW_NAMES,
  CONSUMER_POLICY_REVIEW_PRICES_IN_CENTS,
} from '@shared/consumerPlan';
import { DetailList } from '../components/figures';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useIsPolicyReviewIncluded, usePolicyReviewCheckout } from '../hooks/use-policy-review';
import { money } from '../lib/format';
import { usePolicyReviewDraft } from '../stores/policy-review-draft-store';
import type { ConsumerStepProps } from './step-props';

export function ReviewCheckoutStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { reviewType, documents, responseDeadline } = usePolicyReviewDraft();
  const { startCheckout, isStarting, isError } = usePolicyReviewCheckout(clientId);
  const isIncluded = useIsPolicyReviewIncluded(clientId);

  if (!reviewType || documents.length === 0) {
    return (
      <StepFrame
        step="review-checkout"
        title="Let’s add your documents first."
        subtitle="Your review needs a review type and at least one document."
        primary={{ label: 'Choose a review', onClick: () => goTo('existing-policy', { replace: true }) }}
      />
    );
  }

  const fee = money(CONSUMER_POLICY_REVIEW_PRICES_IN_CENTS[reviewType] / 100);
  const failure = isIncluded ? 'We couldn’t send your request.' : 'We couldn’t open checkout.';

  return (
    <StepFrame
      step="review-checkout"
      title="Ready for us to review your policy?"
      subtitle={
        reviewType === 'rate'
          ? 'See how each offered option supports your future care.'
          : 'See how your current coverage supports your future care.'
      }
      onBack={() => back('review-upload')}
      help={
        <p>
          {reviewType === 'rate'
            ? 'We’ll analyze your current policy and the options your carrier actually offered, using the same care scenario for each.'
            : 'We’ll analyze the policy terms and add the coverage to your care plan.'}{' '}
          {isIncluded
            ? 'It’s included with your self-funding access, so there’s nothing to pay.'
            : 'The PDF is included; there’s no separate report fee.'}
        </p>
      }
      primary={{
        label: isIncluded ? 'Request my review' : `Pay ${fee} with Stripe`,
        onClick: () => startCheckout(),
        loading: isStarting,
      }}
      error={isError ? `${failure} Please try again.` : null}
      next={isIncluded ? 'get an email confirmation.' : 'pay securely through Stripe, then get an email confirmation.'}
    >
      <DetailList
        rows={[
          {
            label: CONSUMER_POLICY_REVIEW_NAMES[reviewType],
            value: isIncluded ? 'Included with self-funding access' : fee,
          },
          { label: 'Documents added', value: documents.length },
          { label: 'Updated online care plan', value: 'Included' },
          { label: 'PDF with the analysis', value: 'Included' },
          ...(reviewType === 'rate' && responseDeadline
            ? [{ label: 'Carrier response deadline', value: responseDeadline }]
            : []),
        ]}
      />
      <p className="text-sm">
        <strong>We aim for 24–48 hours</strong> after receiving complete documents. Some reviews may take up
        to 7 business days.
      </p>
    </StepFrame>
  );
}
